import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Play, Pause, Download, Edit3, RotateCcw, Clock, Maximize2, Minimize2, ToggleLeft, ToggleRight } from 'lucide-react';

export const parseTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map((p) => parseFloat(p));
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parseFloat(timeStr) || 0;
}; 

const ASPECT_CLASS = {
  '16:9': 'aspect-video',
  '1:1': 'aspect-square',
  '9:16': 'aspect-[9/16]',
};

// Largeurs par défaut de la bulle
const DEFAULT_SIZE_BY_ASPECT = {
  '16:9': 1280,
  '1:1' : 780,
  '9:16': 320,
};

// Clip par défaut pour intro/outro
const INTRO_OUTRO_URL = "https://pub-5fa6b6e240814e96bc2f966cc1a5e705.r2.dev/intro-2.mp4";
const INTRO_OUTRO_DURATION = 3; // Durée en secondes

const SimpleVideoPreview = ({
  videoFile,
  segments = [],
  title = "",
  onExport,
  isExporting = false,
  defaultAspect = '16:9',
  onAspectChange,
  sizeByAspect,
  defaultFitMode = 'contain',
  onViewportChange,
  defaultAddIntro = false,
  defaultAddOutro = false,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [aspect, setAspect] = useState(['16:9', '1:1', '9:16'].includes(defaultAspect) ? defaultAspect : '16:9');
  const [fitMode, setFitMode] = useState(defaultFitMode === 'cover' ? 'cover' : 'contain');
  const [faceAware, setFaceAware] = useState(false); // ✅ AJOUTÉ : État manquant
  const [isEditing, setIsEditing] = useState(false);
  const seekingRef = useRef(false);

  // Ajout intro/outro (facultatifs)
  const [addIntro, setAddIntro] = useState(!!defaultAddIntro);
  const [addOutro, setAddOutro] = useState(!!defaultAddOutro);

  const widths = { ...DEFAULT_SIZE_BY_ASPECT, ...(sizeByAspect || {}) };
  const wrapperMaxWidth = widths[aspect] ?? widths['16:9'];

  const handleAspect = (next) => {
    setAspect(next);
    if (typeof onAspectChange === 'function') onAspectChange(next);
    if (typeof onViewportChange === 'function') onViewportChange({ aspect: next, fitMode, faceAware });
  };

  const toggleFitMode = () => {
    setFitMode(m => {
      const nm = (m === 'cover' ? 'contain' : 'cover');
      if (typeof onViewportChange === 'function') onViewportChange({ aspect, fitMode: nm, faceAware });
      return nm;
    });
  };

  // URL stable
  const src = useMemo(() => {
    if (!videoFile) return null;
    return typeof videoFile === 'string' ? videoFile : URL.createObjectURL(videoFile);
  }, [videoFile]);

  useEffect(() => {
    return () => {
      if (src && typeof videoFile !== 'string') {
        try { URL.revokeObjectURL(src); } catch {}
      }
    };
  }, [src, videoFile]);

  // Normalisation et tri des segments principaux
  const normSegments = useMemo(() => {
    const s = (segments || [])
      .map(seg => ({
        start: Number(seg.start) || 0,
        end: Number(seg.end) || 0,
        label: seg.label || ''
      }))
      .filter(seg => seg.end > seg.start + 0.05)
      .sort((a, b) => a.start - b.start);
    return s;
  }, [segments]);

  // Construction de la séquence finale avec intro/outro
  const finalSequence = useMemo(() => {
    const sequence = [];
    
    // Intro
    if (addIntro) {
      sequence.push({
        type: 'intro',
        start: 0,
        end: INTRO_OUTRO_DURATION,
        length: INTRO_OUTRO_DURATION,
        label: 'Intro',
        url: INTRO_OUTRO_URL
      });
    }
    
    // Segments principaux
    normSegments.forEach(seg => {
      sequence.push({
        type: 'main',
        start: seg.start,
        end: seg.end,
        length: seg.end - seg.start,
        label: seg.label,
        url: src
      });
    });
    
    // Outro
    if (addOutro) {
      sequence.push({
        type: 'outro',
        start: 0,
        end: INTRO_OUTRO_DURATION,
        length: INTRO_OUTRO_DURATION,
        label: 'Outro',
        url: INTRO_OUTRO_URL
      });
    }
    
    return sequence;
  }, [normSegments, addIntro, addOutro, src]);

  // Pré-calcul cumulatif pour scrubbing séquence
  const cumSegments = useMemo(() => {
    let cum = 0;
    return finalSequence.map(seg => {
      const item = { ...seg, cumStart: cum };
      cum += seg.length;
      return item;
    });
  }, [finalSequence]);

  const totalDuration = useMemo(
    () => cumSegments.reduce((acc, seg) => acc + seg.length, 0),
    [cumSegments]
  );

  const sequenceTime = useMemo(() => {
    let total = 0;
    for (let i = 0; i < cumSegments.length; i++) {
      const seg = cumSegments[i];
      if (seg.type === 'main') {
        if (currentTime < seg.start) break;
        if (currentTime >= seg.end) { total += seg.length; continue; }
        total += (currentTime - seg.start);
        break;
      } else {
        // Pour intro/outro, on utilise la position dans la séquence
        const currentSeg = cumSegments[currentSegmentIndex];
        if (currentSeg && currentSeg === seg) {
          total += Math.min(seg.length, currentTime);
          break;
        } else if (i < currentSegmentIndex) {
          total += seg.length;
        }
      }
    }
    return total;
  }, [cumSegments, currentTime, currentSegmentIndex]);

  // Chargement source/segments
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    setIsPlaying(false);
    setCurrentSegmentIndex(0);
    seekingRef.current = false;

    v.pause();
    
    // Charger la première source (intro ou premier segment)
    if (cumSegments.length > 0) {
      const firstSeg = cumSegments[0];
      v.src = firstSeg.url || src;
      v.preload = 'auto';
      v.load();
    }
  }, [src, cumSegments]);

  const seekTo = (t) => {
    const v = videoRef.current;
    if (!v) return;
    seekingRef.current = true;
    v.currentTime = Math.max(0, t + 0.001);
  };

  const seekSequence = (seqT) => {
    let t = Math.max(0, Math.min(totalDuration, seqT));
    
    // Trouver le segment correspondant dans la séquence
    for (let i = 0; i < cumSegments.length; i++) {
      const seg = cumSegments[i];
      if (t <= seg.length) {
        setCurrentSegmentIndex(i);
        
        // Changer la source si nécessaire
        const v = videoRef.current;
        if (v && v.src !== seg.url) {
          v.src = seg.url;
          v.load();
        }
        
        if (seg.type === 'main') {
          seekTo(seg.start + t);
        } else {
          seekTo(seg.start + t);
        }
        return;
      }
      t -= seg.length;
    }
  };

  const onLoadedMetadata = () => {
    if (cumSegments.length > 0) {
      const firstSeg = cumSegments[0];
      if (firstSeg.type === 'main') {
        seekTo(firstSeg.start);
      } else {
        seekTo(firstSeg.start);
      }
    }
  };

  const onSeeked = () => {
    seekingRef.current = false;
    if (isPlaying) {
      videoRef.current?.play().catch(() => {});
    }
  };

  const onTimeUpdate = () => {
    if (seekingRef.current || !cumSegments.length) return;
    const v = videoRef.current;
    setCurrentTime(v.currentTime);

    const seg = cumSegments[currentSegmentIndex];
    if (!seg) return;

    // Logique différente selon le type de segment
    if (seg.type === 'main') {
      if (v.currentTime >= seg.end - 0.03) {
        playNextSegment();
      }
    } else {
      // Pour intro/outro, on base sur la durée
      if (v.currentTime >= seg.start + seg.length - 0.03) {
        playNextSegment();
      }
    }
  };

  const playNextSegment = () => {
    const next = currentSegmentIndex + 1;
    if (next < cumSegments.length) {
      const nextSeg = cumSegments[next];
      setCurrentSegmentIndex(next);
      
      // Changer la source si nécessaire
      const v = videoRef.current;
      if (v.src !== nextSeg.url) {
        v.src = nextSeg.url;
        v.addEventListener('loadedmetadata', () => {
          seekTo(nextSeg.start);
        }, { once: true });
        v.load();
      } else {
        seekTo(nextSeg.start);
      }
    } else {
      // Fin de la séquence
      setIsPlaying(false);
      videoRef.current?.pause();
      setCurrentSegmentIndex(0);
      const firstSeg = cumSegments[0];
      if (videoRef.current?.src !== firstSeg.url) {
        videoRef.current.src = firstSeg.url;
        videoRef.current?.load();
      }
      seekTo(firstSeg.start);
    }
  };

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v || !cumSegments.length) return;

    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
      return;
    }

    const seg = cumSegments[currentSegmentIndex];
    
    // S'assurer que la bonne source est chargée
    if (v.src !== seg.url) {
      v.src = seg.url;
      v.addEventListener('loadedmetadata', async () => {
        seekTo(seg.start);
        try {
          await v.play();
          setIsPlaying(true);
        } catch (e) {
          v.muted = true;
          try {
            await v.play();
            setIsPlaying(true);
          } catch (err) {
            console.warn('Play failed:', err);
          }
        }
      }, { once: true });
      v.load();
      return;
    }

    // Vérifier la position
    if (seg.type === 'main') {
      if (v.currentTime < seg.start || v.currentTime >= seg.end) {
        seekTo(seg.start);
      }
    } else {
      if (v.currentTime < seg.start || v.currentTime >= seg.start + seg.length) {
        seekTo(seg.start);
      }
    }

    try {
      await v.play();
      setIsPlaying(true);
    } catch (e) {
      v.muted = true;
      try {
        await v.play();
        setIsPlaying(true);
      } catch (err) {
        console.warn('Play failed:', err);
      }
    }
  };

  const resetToStart = () => {
    const v = videoRef.current;
    if (!v || !cumSegments.length) return;
    v.pause();
    setIsPlaying(false);
    setCurrentSegmentIndex(0);
    const firstSeg = cumSegments[0];
    if (v.src !== firstSeg.url) {
      v.src = firstSeg.url;
      v.load();
    }
    seekTo(firstSeg.start);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="rounded-2xl border border-zinc-700 bg-zinc-900/50 overflow-hidden mx-auto w-full"
      style={{ maxWidth: wrapperMaxWidth, transition: 'max-width 240ms ease' }}
    >
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{title || "Aperçu du montage"}</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {cumSegments.length} segment{cumSegments.length > 1 ? 's' : ''} • Durée: {formatTime(totalDuration)}
              {addIntro && <span className="ml-2 text-emerald-400">+ Intro</span>}
              {addOutro && <span className="ml-2 text-emerald-400">+ Outro</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Player + sélecteurs en haut à droite */}
      <div className={`relative bg-black w-full ${ASPECT_CLASS[aspect] || ASPECT_CLASS['16:9']}`}>
        {/* Boutons de format + Fit/Fill */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-black/60 backdrop-blur-sm border border-white/10 rounded-xl p-1">
          {['16:9', '1:1', '9:16'].map((opt) => (
            <button
              key={opt}
              onClick={() => handleAspect(opt)}
              className={`px-2.5 py-1.5 text-xs rounded-lg transition ${
                aspect === opt ? 'bg-white text-black' : 'text-white/85 hover:bg-white/10'
              }`}
              title={`Afficher en ${opt}`}
            >
              {opt}
            </button>
          ))}
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button
            onClick={toggleFitMode}
            className="px-2.5 py-1.5 text-xs rounded-lg transition text-white/85 hover:bg-white/10"
            title={fitMode === 'contain' ? 'Passer en Fill (cover)' : 'Passer en Fit (contain)'}
          >
            {fitMode === 'contain' ? (
              <span className="inline-flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" /> Fill</span>
            ) : (
              <span className="inline-flex items-center gap-1"><Minimize2 className="w-3.5 h-3.5" /> Fit</span>
            )}
          </button>
        </div>

        {/* Vidéo */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full ${fitMode === 'cover' ? 'object-cover' : 'object-contain'}`}
          playsInline
          muted={false}
          preload="auto"
          onLoadedMetadata={onLoadedMetadata}
          onSeeked={onSeeked}
          onTimeUpdate={onTimeUpdate}
        />

        {/* Contrôles bas */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition"
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
            </button>

            <button
              onClick={resetToStart}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition"
              title="Revenir au début"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* SCRUBBER (seek) */}
            <div className="flex-1 flex flex-col">
              <input
                type="range"
                min={0}
                max={Math.max(totalDuration, 0.01)}
                step={0.01}
                value={sequenceTime}
                onChange={(e) => seekSequence(parseFloat(e.target.value))}
                className="w-full accent-white"
                title="Défilement de la séquence"
                disabled={totalDuration <= 0}
              />
              <div className="flex justify-between text-[11px] text-white/80 mt-1 font-mono">
                <span>{formatTime(sequenceTime)}</span>
                <span>{formatTime(totalDuration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des segments */}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Séquence finale
          </h4>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={isExporting}
              onClick={() => setAddIntro(v => !v)}
              className={`px-3 py-1.5 rounded-md text-xs tracking-wide uppercase transition
                ${addIntro
                  ? 'bg-emerald-500/20 border border-emerald-400/40 text-emerald-200'
                  : 'bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 text-white/90'}`}
              title="Ajouter/retirer l'intro"
            >
              INTRO
            </button>
            <button
              type="button"
              disabled={isExporting}
              onClick={() => setAddOutro(v => !v)}
              className={`px-3 py-1.5 rounded-md text-xs tracking-wide uppercase transition
                ${addOutro
                  ? 'bg-emerald-500/20 border border-emerald-400/40 text-emerald-200'
                  : 'bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 text-white/90'}`}
              title="Ajouter/retirer l'outro"
            >
              OUTRO
            </button>
          </div>
        </div>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {cumSegments.map((segment, index) => (
            <button
              key={index}
              onClick={() => { 
                setCurrentSegmentIndex(index); 
                const v = videoRef.current;
                if (v && v.src !== segment.url) {
                  v.src = segment.url;
                  v.addEventListener('loadedmetadata', () => {
                    seekTo(segment.start);
                  }, { once: true });
                  v.load();
                } else {
                  seekTo(segment.start); 
                }
              }}
              className={`w-full text-left p-2 rounded-lg text-xs flex items-center justify-between transition ${
                index === currentSegmentIndex ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-zinc-800/50 hover:bg-zinc-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded text-white text-[10px] flex items-center justify-center ${
                  segment.type === 'intro' ? 'bg-emerald-500' : 
                  segment.type === 'outro' ? 'bg-purple-500' : 'bg-blue-500'
                }`}>
                  {segment.type === 'intro' ? 'I' : segment.type === 'outro' ? 'O' : index - (addIntro ? 1 : 0) + 1}
                </span>
                <span className="font-mono">
                  {segment.type === 'main' ? 
                    `${formatTime(segment.start)} → ${formatTime(segment.end)}` :
                    `${segment.label} (${formatTime(segment.length)})`
                  }
                </span>
              </div>
              <div className="text-zinc-400">
                {formatTime(segment.length)}
              </div>
              {segment.label && segment.type === 'main' && (
                <div className="text-zinc-300 truncate max-w-[120px]">
                  {segment.label}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-zinc-700 bg-zinc-900/30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onExport?.({
              aspect,
              fitMode,
              faceAware,
              addIntro,
              addOutro,
              introUrl: INTRO_OUTRO_URL,
              outroUrl: INTRO_OUTRO_URL,
            })}
            disabled={isExporting || cumSegments.length === 0}
            className="flex-1 bg-white text-black py-3 px-4 rounded-xl font-medium hover:bg-zinc-200 transition disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
          >
            {isExporting ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Export en cours...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Exporter cette version
              </>
            )}
          </button>

          <button
            type="button"
            disabled={isExporting}
            onClick={() => setIsEditing(v => !v)}
            aria-pressed={isEditing}
            title={isEditing ? "Désactiver le mode édition" : "Activer le mode édition"}
            className={[
              "py-3 px-4 rounded-xl transition disabled:opacity-50 disabled:pointer-events-none",
              "flex items-center gap-2 border",
              isEditing
                ? "bg-emerald-500 text-black border-emerald-400 hover:bg-emerald-400"
                : "bg-zinc-700 text-white border-zinc-600 hover:bg-zinc-600"
            ].join(" ")}
          >
            {isEditing ? (
              <>
                <ToggleRight className="w-4 h-4" />
                Édition activée
              </>
            ) : (
              <>
                <ToggleLeft className="w-4 h-4" />
                Modifier
              </>
            )}
          </button>
        </div>

        {/* Option visible uniquement en 9:16 */}
        {aspect === '9:16' && (
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-zinc-300">
            <input
              type="checkbox"
              className="accent-white"
              checked={faceAware}
              disabled={isExporting}
              onChange={(e) => {
                setFaceAware(e.target.checked);
                if (typeof onViewportChange === 'function') {
                  onViewportChange({ aspect, fitMode, faceAware: e.target.checked });
                }
              }}
            />
            Utiliser la détection de visages pour centrer (peut ralentir l'export)
          </label>
        )}

        <p className="text-xs text-zinc-400 mt-3 text-center">
          Prévisualise ton montage avec intro/outro avant l'export final
        </p>
      </div>
    </div>
  );
};

export default SimpleVideoPreview;
